/**
 * One agent-step SSE connection per URL, shared by every consumer of it.
 *
 * The same problem as the screencast stream, on the other endpoint: a single
 * browser session has several components reading its steps at once — the
 * inline card's `BrowserStepsViewer`, the expanded layover's, and
 * `BrowserLiveView`'s pre-cache — and each opened its own SSE. Measured in the
 * router log on one session, after the screencast had been deduplicated:
 *
 *   GET /sessions/8XsS8E-C8vosDkO5BmjkG/screencast "200 OK"  17:00:55
 *   GET /sessions/8XsS8E-C8vosDkO5BmjkG/stream     "200 OK"  17:00:55
 *   GET /sessions/8XsS8E-C8vosDkO5BmjkG/stream     "200 OK"  17:01:20
 *   GET /sessions/8XsS8E-C8vosDkO5BmjkG/stream     "200 OK"  17:01:26
 *
 * Against Chrome's six connections per origin over HTTP/1.1, three streams for
 * one session's steps is most of the page's budget.
 *
 * Keyed by URL and ref-counted, so the count is one per session however many
 * components mount. See `screencast-stream` for the same pattern and the
 * reasoning behind sharing rather than coordinating between mount sites.
 *
 * @module browser-automation/hooks/agent-stream-connection
 */

import { fetchEventSource } from '@microsoft/fetch-event-source';

import { captureException } from '@/modules/sentry';

import {
  type EAgentStatus,
  type IAgentStep,
  type IAgentStreamMessage,
} from '../types';

/** Fatal HTTP status codes that should not be retried */
const FATAL_HTTP_STATUSES = new Set([404, 410]);

/** Maximum number of retries for transient errors */
const MAX_TRANSIENT_RETRIES = 5;

/** Maximum number of stream caches to retain (LRU eviction). */
const MAX_CACHE_SIZE = 50;

/** Maximum number of fatal URL entries to retain. Oldest are evicted when exceeded. */
const MAX_FATAL_URL_CACHE_SIZE = 200;

/**
 * Grace period between the last consumer leaving and the connection closing.
 * Covers the expand handoff and StrictMode's double-invoked effects — see
 * `STREAM_LINGER_MS` in `screencast-stream`.
 */
export const AGENT_STREAM_LINGER_MS = 1000;

/** localStorage keys for persistent caches */
const LS_STREAM_KEY = 'bf:agentStream:cache';
const LS_FATAL_URLS_KEY = 'bf:agentStream:fatalUrls';

/**
 * Cached agent stream data, keyed by streamUrl.
 * Survives component unmount/remount so steps are not lost when
 * the dialog closes and the browser pod is already terminated.
 */
interface ICachedAgentStream {
  steps: IAgentStep[];
  agentStatus: EAgentStatus | null;
}

/** Everything a consumer renders from. Replayed whole on subscribe. */
export interface IAgentStreamSnapshot {
  steps: IAgentStep[];
  agentStatus: EAgentStatus | null;
  isConnecting: boolean;
  isConnected: boolean;
  error: string | null;
}

/**
 * Module-level cache for agent stream data.
 * Hydrated from localStorage on module load; persisted on updates.
 * @internal Exported for testing only.
 */
export const streamCache: Map<string, ICachedAgentStream> = (() => {
  const map = new Map<string, ICachedAgentStream>();
  try {
    const raw = localStorage.getItem(LS_STREAM_KEY);
    if (raw) {
      const entries: [string, ICachedAgentStream][] = JSON.parse(raw);
      for (const [k, v] of entries) map.set(k, v);
    }
  } catch {
    /* corrupt data — start fresh */
  }
  return map;
})();

/**
 * Module-level cache of URLs that returned fatal HTTP errors (404/410)
 * or exhausted transient retries. Hydrated from localStorage on module load.
 * @internal Exported for testing only.
 */
export const fatalUrlCache: Set<string> = (() => {
  const set = new Set<string>();
  try {
    const raw = localStorage.getItem(LS_FATAL_URLS_KEY);
    if (raw) {
      const items: string[] = JSON.parse(raw);
      for (const item of items) set.add(item);
    }
  } catch {
    /* corrupt data — start fresh */
  }
  return set;
})();

/** Evict oldest entries if over capacity, then persist to localStorage. */
function persistFatalUrls(): void {
  while (fatalUrlCache.size > MAX_FATAL_URL_CACHE_SIZE) {
    const oldest = fatalUrlCache.values().next().value;
    if (oldest !== undefined) fatalUrlCache.delete(oldest);
  }
  try {
    localStorage.setItem(LS_FATAL_URLS_KEY, JSON.stringify([...fatalUrlCache]));
  } catch {
    /* storage full — non-critical */
  }
}

/** Persist stream cache to localStorage. */
function persistStreamCache(): void {
  try {
    const entries = [...streamCache.entries()].slice(-MAX_CACHE_SIZE);
    localStorage.setItem(LS_STREAM_KEY, JSON.stringify(entries));
  } catch {
    /* storage full — non-critical */
  }
}

/**
 * Updates the stream cache for a given URL, evicting the oldest
 * entry when the cache exceeds MAX_CACHE_SIZE.
 */
function updateStreamCache(
  url: string,
  update: Partial<ICachedAgentStream>,
): void {
  const existing = streamCache.get(url);
  const entry: ICachedAgentStream = {
    steps: update.steps ?? existing?.steps ?? [],
    agentStatus:
      update.agentStatus !== undefined
        ? update.agentStatus
        : (existing?.agentStatus ?? null),
  };

  // Delete + re-set to move to end (most-recently-used)
  streamCache.delete(url);
  streamCache.set(url, entry);

  // Evict oldest entries if over capacity
  while (streamCache.size > MAX_CACHE_SIZE) {
    const oldest = streamCache.keys().next().value;
    if (oldest !== undefined) {
      streamCache.delete(oldest);
    }
  }

  persistStreamCache();
}

type TListener = (snapshot: IAgentStreamSnapshot) => void;

interface IStream {
  abort: AbortController;
  snapshot: IAgentStreamSnapshot;
  listeners: Set<TListener>;
  /** Pending teardown, cancelled if a consumer re-subscribes within the grace period. */
  lingerTimer: ReturnType<typeof setTimeout> | null;
  /** Kept current so a reconnect mints a fresh token rather than replaying a stale one. */
  getToken: () => Promise<string | null>;
  extraHeaders: Record<string, string>;
  /**
   * The largest cap any consumer asked for. Trimming to the smallest would
   * silently shortchange a consumer that wanted the full history.
   */
  maxSteps: number;
}

const streams = new Map<string, IStream>();

function emit(stream: IStream, patch: Partial<IAgentStreamSnapshot>): void {
  stream.snapshot = { ...stream.snapshot, ...patch };
  for (const listener of stream.listeners) listener(stream.snapshot);
}

function openStream(url: string, stream: IStream): void {
  let transientRetryCount = 0;
  let isFatalError = false;
  const { abort } = stream;

  function handleMessage(message: IAgentStreamMessage): void {
    switch (message.type) {
      case 'history': {
        const historySteps = message.data.steps as IAgentStep[] | undefined;
        const status = message.data.status as EAgentStatus | undefined;
        emit(stream, {
          ...(historySteps ? { steps: historySteps } : {}),
          ...(status ? { agentStatus: status } : {}),
        });
        updateStreamCache(url, { steps: historySteps, agentStatus: status });
        break;
      }

      case 'step': {
        if (
          message.data.step_number === undefined ||
          !message.data.description
        ) {
          break;
        }
        const newStep: IAgentStep = {
          step_number: message.data.step_number as number,
          description: message.data.description as string,
          timestamp: (message.data.timestamp as string) ?? '',
        };
        const updated = [...stream.snapshot.steps, newStep];
        const trimmed =
          updated.length > stream.maxSteps
            ? updated.slice(-stream.maxSteps)
            : updated;
        emit(stream, { steps: trimmed });
        updateStreamCache(url, { steps: trimmed });
        break;
      }

      case 'status': {
        const status = message.data.status as EAgentStatus | undefined;
        emit(stream, {
          ...(status ? { agentStatus: status } : {}),
          ...(message.data.error
            ? { error: message.data.error as string }
            : {}),
        });
        if (status) updateStreamCache(url, { agentStatus: status });
        break;
      }

      case 'error':
        if (message.data.message) {
          emit(stream, { error: message.data.message as string });
        }
        break;

      default:
        break;
    }
  }

  async function connect(): Promise<void> {
    let token: string | null = null;
    try {
      token = await stream.getToken();
    } catch {
      // Auth may not be required for all environments
    }

    if (abort.signal.aborted) return;

    const headers: Record<string, string> = { ...stream.extraHeaders };
    if (!headers['X-E2E-Test-Mode'] && token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      await fetchEventSource(url, {
        signal: abort.signal,
        headers,
        openWhenHidden: true, // Keep streaming when tab is backgrounded

        onopen: async response => {
          if (response.ok) {
            transientRetryCount = 0;
            isFatalError = false;
            emit(stream, {
              isConnecting: false,
              isConnected: true,
              error: null,
            });
          } else if (FATAL_HTTP_STATUSES.has(response.status)) {
            // Browser pod gone — terminal, do not retry.
            // Abort BEFORE throwing: fetchEventSource retries internally
            // when onopen throws without checking onerror first.
            isFatalError = true;
            fatalUrlCache.add(url);
            persistFatalUrls();
            emit(stream, {
              isConnecting: false,
              isConnected: false,
              error: `Agent stream ended: ${response.status}`,
            });
            abort.abort();
            throw new Error(
              `Agent stream ended: ${response.status} ${response.statusText}`,
            );
          } else {
            throw new Error(
              `Agent stream connection failed: ${response.status} ${response.statusText}`,
            );
          }
        },

        onmessage: event => {
          // Empty / whitespace-only frames (SSE keepalives, truncated EOF)
          // are expected — skip them without treating them as parse failures.
          const raw = event.data?.trim();
          if (!raw) return;
          try {
            const message: IAgentStreamMessage = JSON.parse(raw);
            handleMessage(message);
          } catch (e) {
            captureException(e, {
              tags: {
                module: 'browser-automation',
                action: 'parse-sse-message',
              },
              extra: { streamUrl: url, eventType: event.event },
            });
          }
        },

        onerror: err => {
          // If already aborted (e.g. from onopen 404 handler),
          // throw to tell fetchEventSource to stop retrying.
          if (abort.signal.aborted) throw err;

          // Fatal error from onopen (404/410) — stop immediately
          if (isFatalError) {
            abort.abort();
            throw err;
          }

          transientRetryCount++;
          if (transientRetryCount >= MAX_TRANSIENT_RETRIES) {
            // Do NOT add to fatalUrlCache here — only 404/410 HTTP responses
            // should be considered fatal (pod gone). Transient failures
            // (network hiccups, proxy timeouts) should only stop THIS
            // stream's retries, not block a later one.
            emit(stream, {
              isConnecting: false,
              isConnected: false,
              error: 'Agent stream connection lost',
            });
            abort.abort();
            throw err;
          }

          emit(stream, { error: 'Connection error, retrying...' });
          // Return undefined to let fetch-event-source retry with backoff
        },

        onclose: () => {
          if (!abort.signal.aborted) {
            emit(stream, { isConnecting: false, isConnected: false });
          }
        },
      });
    } catch {
      // Errors from thrown onerror/onopen land here — state already emitted
      if (!abort.signal.aborted) {
        abort.abort();
      }
    }
  }

  connect();
}

const EMPTY_SNAPSHOT: IAgentStreamSnapshot = {
  steps: [],
  agentStatus: null,
  isConnecting: false,
  isConnected: false,
  error: null,
};

/**
 * Attach a consumer to `url`'s step stream, opening it if this is the first.
 *
 * The current snapshot — including steps already received, or restored from
 * the persisted cache when the stream is new — is delivered synchronously, so
 * a consumer that joins mid-session renders the history it missed.
 *
 * @returns unsubscribe. The connection closes once the last consumer detaches
 * and the linger period passes.
 */
export function subscribeToAgentStream(
  url: string,
  getToken: () => Promise<string | null>,
  extraHeaders: Record<string, string>,
  maxSteps: number,
  listener: TListener,
): () => void {
  const existing = streams.get(url);

  if (existing) {
    if (existing.lingerTimer !== null) {
      clearTimeout(existing.lingerTimer);
      existing.lingerTimer = null;
    }
    // A later consumer's token getter is as good as the first one's and
    // cannot be more stale, and the first one may be what unmounts.
    existing.getToken = getToken;
    existing.extraHeaders = extraHeaders;
    existing.maxSteps = Math.max(existing.maxSteps, maxSteps);
    existing.listeners.add(listener);
    listener(existing.snapshot);
    return () => detach(url, listener);
  }

  const cached = streamCache.get(url);
  const stream: IStream = {
    abort: new AbortController(),
    snapshot: {
      ...EMPTY_SNAPSHOT,
      steps: cached?.steps ?? [],
      agentStatus: cached?.agentStatus ?? null,
    },
    listeners: new Set([listener]),
    lingerTimer: null,
    getToken,
    extraHeaders,
    maxSteps,
  };
  streams.set(url, stream);

  // Skip connecting to URLs that already returned fatal 404/410 errors.
  // This prevents repeated network requests to pods that no longer exist.
  if (fatalUrlCache.has(url)) {
    emit(stream, { error: 'Agent stream ended: 404' });
  } else {
    emit(stream, { isConnecting: true, error: null });
    openStream(url, stream);
  }

  return () => detach(url, listener);
}

function detach(url: string, listener: TListener): void {
  const stream = streams.get(url);
  if (!stream) return;
  stream.listeners.delete(listener);
  if (stream.listeners.size > 0 || stream.lingerTimer !== null) return;
  stream.lingerTimer = setTimeout(() => {
    stream.lingerTimer = null;
    if (stream.listeners.size > 0) return;
    stream.abort.abort();
    streams.delete(url);
  }, AGENT_STREAM_LINGER_MS);
}

/** Test seam — streams are module state and outlive a single render. */
export function resetAgentStreams(): void {
  for (const [url, stream] of streams) {
    if (stream.lingerTimer !== null) clearTimeout(stream.lingerTimer);
    stream.abort.abort();
    streams.delete(url);
  }
}

/** @internal Exported for testing only. */
export function countAgentStreams(): number {
  return streams.size;
}

/** @internal Exported for testing only. */
export function countAgentStreamListeners(url: string): number {
  return streams.get(url)?.listeners.size ?? 0;
}
